import * as PIXI from 'pixi.js';
import type { MapObject } from '../../../../models/mapObject';
import { buildAdminRadialMenuOptions } from '../../admin/adminRadialMenu';

/** MapCanvas-owned admin overlay state — not store-backed. */
export interface AdminOverlaysHostProps {
  adminResizeDims: { w: number; h: number } | null;
  showAdminRadialMenu: boolean;
  adminRadialMenuMapObject: { mapObject: MapObject; marker: PIXI.Container } | null;
  /** Snapshot of objectModes at menu open — avoids reading sceneRef during render. */
  adminRadialObjectMode: 'dnd' | 'resize';
  getAdminRadialPositions: (count: number) => Array<{ xOffset: number; yOffset: number }>;
  highlightedAdminAction: string | null;
  adminRadialMenuPosition: { x: number; y: number };
  closeAdminRadialMenu: () => void;
  executeAdminRadialAction: (actionKey: string, mapObject: MapObject, marker: PIXI.Container) => void;
}

/**
 * Admin resize-dims overlay + radial menu pair. Tightly coupled admin UX —
 * extracted together. JSX moved verbatim.
 */
export default function AdminOverlaysHost({
  adminResizeDims,
  showAdminRadialMenu,
  adminRadialMenuMapObject,
  adminRadialObjectMode,
  getAdminRadialPositions,
  highlightedAdminAction,
  adminRadialMenuPosition,
  closeAdminRadialMenu,
  executeAdminRadialAction,
}: AdminOverlaysHostProps) {
  return (
        <>
        {/* Admin resize dimensions overlay */}
        {adminResizeDims && (
          <div
            style={{
              position: 'fixed',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 3010,
              pointerEvents: 'none',
              background: 'rgba(0, 0, 0, 0.8)',
              color: '#fff',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 700,
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
            }}
          >
            W: {adminResizeDims.w} &nbsp; H: {adminResizeDims.h}
          </div>
        )}

        {/* Admin radial menu — drag-to-option (selection on pointerup in setupAdminDragAndDrop) */}
        {showAdminRadialMenu && adminRadialMenuMapObject && (() => {
          const menuItems = buildAdminRadialMenuOptions(
            adminRadialObjectMode,
            adminRadialMenuMapObject.mapObject.type,
          );
          const positions = getAdminRadialPositions(menuItems.length);
          // Islands use tap-to-open, so items must be clickable.
          // Regular objects use long-press-drag-to-select, so pointer events stay 'none'.
          const isIslandMenu = adminRadialMenuMapObject.mapObject.type.startsWith('island_');

          return (
            <>
              {/* Backdrop — click to dismiss (only for islands; regular objects use drag-to-select) */}
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 3000,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  pointerEvents: isIslandMenu ? 'auto' : 'none',
                }}
                onClick={() => closeAdminRadialMenu()}
              />
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 3001,
                  pointerEvents: 'none',
                }}
              >
                {menuItems.map((item, index) => {
                  const pos = positions[index] ?? { xOffset: 0, yOffset: 0 };
                  const x = adminRadialMenuPosition.x + pos.xOffset;
                  const y = adminRadialMenuPosition.y + pos.yOffset;
                  const isHighlighted = highlightedAdminAction === item.key;

                  return (
                    <div
                      key={item.key}
                      style={{
                        position: 'fixed',
                        left: `${x}px`,
                        top: `${y}px`,
                        transform: `translate(-50%, -50%) scale(${isHighlighted ? 1.08 : 1})`,
                        zIndex: 3002,
                        pointerEvents: isIslandMenu ? 'auto' : 'none',
                        cursor: isIslandMenu ? 'pointer' : 'default',
                        transition: 'transform 0.1s ease, box-shadow 0.1s ease',
                      }}
                      onClick={isIslandMenu ? (e) => {
                        e.stopPropagation();
                        executeAdminRadialAction(item.key, adminRadialMenuMapObject.mapObject, adminRadialMenuMapObject.marker);
                        closeAdminRadialMenu();
                      } : undefined}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          height: '30px',
                          padding: '0 16px',
                          background: isHighlighted
                            ? (item.key === 'delete' ? '#ffebee' : '#e8f5e9')
                            : (item.key === 'toggle_mode' && adminRadialObjectMode === 'resize' ? '#e8f5e9' : '#fff'),
                          border: item.key === 'delete' ? '1px solid #ff4444' : '1px solid #d0d0d0',
                          borderRadius: '15px',
                          boxShadow: isHighlighted
                            ? '0 4px 16px rgba(0,0,0,0.25)'
                            : '0 2px 8px rgba(0,0,0,0.15)',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: item.key === 'delete' ? '#ff4444' : '#333',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span>{item.icon}</span>
                        <span>{item.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
        </>
  );
}
